/**
 * Service Worker Registration Utility
 * Handles registration of the service worker and push notifications
 */

interface ServiceWorkerRegistrationOptions {
  onUpdateAvailable?: () => void;
  onRegistered?: (registration: ServiceWorkerRegistration) => void;
  onError?: (error: Error) => void;
}

/**
 * Converts a VAPID public key from base64 URL to Uint8Array
 * This is required for the PushManager.subscribe() method
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Registers the service worker
 */
export async function registerServiceWorker(
  options: ServiceWorkerRegistrationOptions = {},
): Promise<ServiceWorkerRegistration | null> {
  const { onUpdateAvailable, onRegistered, onError } = options;

  if (!("serviceWorker" in navigator)) {
    console.warn("[Service Worker] Service workers are not supported");
    return null;
  }

  try {
    // Register the service worker
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });

    console.log("[Service Worker] Registered successfully:", registration.scope);

    // Check for updates
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      console.log("[Service Worker] New service worker found");

      newWorker.addEventListener("statechange", () => {
        if (newWorker.state === "installed") {
          if (navigator.serviceWorker.controller) {
            // New service worker available
            console.log("[Service Worker] New service worker available");
            onUpdateAvailable?.();
          } else {
            // Service worker installed for the first time
            console.log("[Service Worker] Service worker installed");
          }
        }
      });
    });

    // Handle controller change (when new service worker takes control)
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      console.log("[Service Worker] Controller changed - reloading page");
      window.location.reload();
    });

    onRegistered?.(registration);
    return registration;
  } catch (error) {
    console.error("[Service Worker] Registration failed:", error);
    onError?.(error as Error);
    return null;
  }
}

/**
 * Requests notification permission from the user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) {
    console.warn("[Notifications] Notifications are not supported");
    return "denied";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission === "denied") {
    console.warn("[Notifications] Notification permission was previously denied");
    return "denied";
  }

  // Request permission
  const permission = await Notification.requestPermission();
  console.log("[Notifications] Permission status:", permission);
  return permission;
}

/**
 * Subscribes to push notifications
 */
export async function subscribeToPushNotifications(
  registration: ServiceWorkerRegistration,
  publicVapidKey: string,
): Promise<PushSubscription | null> {
  try {
    // Validate VAPID key
    if (!publicVapidKey || publicVapidKey.trim() === "") {
      console.error("[Push] VAPID public key is missing or invalid");
      return null;
    }

    // Check if push manager is available
    if (!registration.pushManager) {
      console.error("[Push] PushManager is not available");
      return null;
    }

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      console.log("[Push] Already subscribed:", subscription.endpoint);
      return subscription;
    }

    // Convert VAPID key
    let applicationServerKey: Uint8Array;
    try {
      applicationServerKey = urlBase64ToUint8Array(publicVapidKey);
    } catch (error) {
      console.error("[Push] Failed to convert VAPID key:", error);
      return null;
    }

    // Subscribe to push notifications with timeout
    const subscribePromise = registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as BufferSource,
    });

    // Add timeout to prevent hanging (10 seconds)
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Subscription timeout")), 10000),
    );

    subscription = (await Promise.race([subscribePromise, timeoutPromise])) as PushSubscription;

    console.log("[Push] Subscribed successfully:", subscription.endpoint);
    return subscription;
  } catch (error) {
    // More specific error handling
    if (error instanceof DOMException) {
      if (error.name === "NotAllowedError") {
        console.warn("[Push] Subscription failed: Permission denied");
      } else if (error.name === "AbortError") {
        // AbortError is common and expected in some scenarios:
        // - Running on localhost (some browsers don't support push on localhost)
        // - Browser push service unavailable
        // - Network issues
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1" ||
          window.location.hostname === "";

        if (isLocalhost) {
          console.info(
            "[Push] Push notifications may not be available on localhost. This is expected.",
          );
        } else {
          console.warn(
            "[Push] Push service unavailable or blocked. Push notifications will not be available.",
          );
        }
      } else {
        console.error("[Push] Subscription failed:", error.name, error.message);
      }
    } else if (error instanceof Error && error.message === "Subscription timeout") {
      console.error("[Push] Subscription failed: Request timed out");
    } else {
      console.error("[Push] Subscription failed:", error);
    }
    return null;
  }
}

/**
 * Unsubscribes from push notifications
 */
export async function unsubscribeFromPushNotifications(
  registration: ServiceWorkerRegistration,
): Promise<boolean> {
  try {
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      console.log("[Push] No active subscription to unsubscribe");
      return true;
    }

    const result = await subscription.unsubscribe();
    console.log("[Push] Unsubscribed:", result);
    return result;
  } catch (error) {
    console.error("[Push] Unsubscribe failed:", error);
    return false;
  }
}

/**
 * Gets the current push subscription
 */
export async function getPushSubscription(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  try {
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error("[Push] Failed to get subscription:", error);
    return null;
  }
}
